import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowUpRight,
  Bot,
  CheckCircle2,
  Database,
  GitBranch,
  History,
  Radar,
  RefreshCw,
  Search,
  ShieldCheck,
  Sparkles,
  Timer
} from "lucide-react";
import { fallbackBrief } from "./data/fallbackBrief";
import type { DailyBrief, HistoryEntry, RecommendationHistory, ToolItem } from "./types";

type Language = "zh" | "en";

const text = {
  zh: {
    nav: ["今日", "雷达", "深度拆解", "历史", "来源"],
    readBrief: "阅读今日简报",
    heroTitle: "每天由 AI Agent 追踪和分析实用 AI 工具。",
    heroBody:
      "ToolPulse AI 持续观察开源项目发布，过滤弱信号，并发布能改变真实工作流的工具结论。",
    openRadar: "打开雷达",
    inspectSource: "查看头号来源",
    toolsAnalyzed: "已分析工具",
    starsScanned: "来源 star 已扫描",
    lastRun: "最近运行",
    rankedTools: "今日工具排行",
    signalOverVolume: "重信号，不堆数量",
    featuredAnalysis: "重点拆解",
    published: "已发布",
    liveData: "实时数据文件",
    seedBrief: "种子简报",
    automation: "自动化",
    sourceHealth: "来源健康",
    runLog: "运行日志",
    toolRadar: "工具雷达",
    evidence: "附带证据的短名单。",
    historyTitle: "历史推荐",
    historyBody:
      "每天筛选出的 AI 工具都会沉淀到这里。你可以回看过往推荐，追踪哪些项目持续活跃，哪些工具已经从短期热点变成值得长期关注的工作流资产。",
    historyEmpty: "历史记录会在下一次每日任务后累积。",
    archiveStatus: "历史已收录",
    scoreLabel: "信号分",
    scoreHelp: "基于热度、更新频率、社区活跃度、项目新鲜度和 AI 主题匹配计算",
    dailyJob: "每日任务",
    linkChecks: "链接检查",
    source: "来源"
  },
  en: {
    nav: ["Today", "Radar", "Deep Dives", "History", "Sources"],
    readBrief: "Read Today's Brief",
    heroTitle: "AI tools, tracked and analyzed by an agent every day.",
    heroBody:
      "ToolPulse AI watches open-source launches, filters weak signals, and publishes concise verdicts on tools that can change real workflows.",
    openRadar: "Open Radar",
    inspectSource: "Inspect Top Source",
    toolsAnalyzed: "tools analyzed",
    starsScanned: "source stars scanned",
    lastRun: "last agent run",
    rankedTools: "Today's ranked tools",
    signalOverVolume: "Signal over volume",
    featuredAnalysis: "Featured analysis",
    published: "Published",
    liveData: "Live data file",
    seedBrief: "Seed brief",
    automation: "Automation",
    sourceHealth: "Source health",
    runLog: "Run log",
    toolRadar: "Tool radar",
    evidence: "Shortlists with evidence attached.",
    historyTitle: "Recommendation history",
    historyBody:
      "Every AI tool selected by the daily scan is archived here, so you can revisit past picks, track which projects stay active, and spot which short-term signals become workflow assets worth following.",
    historyEmpty: "History will accumulate after the next daily run.",
    archiveStatus: "Archived",
    scoreLabel: "Signal",
    scoreHelp: "Calculated from traction, update frequency, community activity, freshness, and AI-topic fit",
    dailyJob: "Daily job",
    linkChecks: "Link checks",
    source: "Source"
  }
};

const formatDate = (value: string, language: Language) =>
  new Intl.DateTimeFormat(language === "zh" ? "zh-CN" : "en", {
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(value));

const archiveSlug = (value: string) =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "item";

function verdictClass(verdict: string) {
  if (verdict === "Worth trying") return "verdict good";
  if (verdict === "Track closely") return "verdict watch";
  if (verdict === "Skip for now") return "verdict skip";
  return "verdict";
}

function scoreTone(score: number) {
  if (score >= 88) return "score high";
  if (score >= 76) return "score medium";
  return "score low";
}

function ScoreBadge({ score, language }: { score: number; language: Language }) {
  const copy = text[language];
  const label = `${copy.scoreLabel} ${score}`;

  return (
    <div className={scoreTone(score)} title={copy.scoreHelp} aria-label={`${label}。${copy.scoreHelp}`}>
      <span>{copy.scoreLabel}</span>
      <strong>{score}</strong>
    </div>
  );
}

function ToolRow({ tool, language }: { tool: ToolItem; language: Language }) {
  const summary = language === "zh" ? tool.summaryZh ?? tool.summary : tool.summary;
  const category = language === "zh" ? tool.categoryZh ?? tool.category : tool.category;
  const starsLabel = language === "zh" ? `${tool.githubStars.toLocaleString()} stars` : `${tool.githubStars.toLocaleString()} stars`;

  return (
    <a className="tool-row" href={tool.sourceUrl} target="_blank" rel="noreferrer">
      <div className="rank">{String(tool.rank).padStart(2, "0")}</div>
      <div className="tool-row-main">
        <div className="tool-title-line">
          <h3>{tool.name}</h3>
          <ArrowUpRight size={16} />
        </div>
        <p>{summary}</p>
        <div className="tool-meta">
          <span>{category}</span>
          <span>{starsLabel}</span>
        </div>
      </div>
      <ScoreBadge score={tool.score} language={language} />
    </a>
  );
}

function ToolCard({ tool, language }: { tool: ToolItem; language: Language }) {
  const category = language === "zh" ? tool.categoryZh ?? tool.category : tool.category;
  const analysis = language === "zh" ? tool.analysisZh ?? tool.analysis : tool.analysis;
  const signals = language === "zh" ? tool.signalsZh ?? tool.signals : tool.signals;
  const verdict = language === "zh" ? tool.verdictZh ?? tool.verdict : tool.verdict;

  return (
    <article className="tool-card">
      <div className="tool-card-top">
        <div>
          <p className="card-label">{category}</p>
          <h3>{tool.name}</h3>
        </div>
        <ScoreBadge score={tool.score} language={language} />
      </div>
      <p className="card-summary">{analysis}</p>
      <div className="signal-list">
        {signals.slice(0, 3).map((signal) => (
          <span key={signal}>{signal}</span>
        ))}
      </div>
      <div className="tool-card-bottom">
        <span className={verdictClass(tool.verdict)}>{verdict}</span>
        <a href={tool.sourceUrl} target="_blank" rel="noreferrer">
          {text[language].source} <ArrowUpRight size={14} />
        </a>
      </div>
    </article>
  );
}

function HistoryDay({ entry, language }: { entry: HistoryEntry; language: Language }) {
  const title = language === "zh" ? entry.featured.titleZh ?? entry.featured.title : entry.featured.title;

  return (
    <article className="history-day">
      <div className="history-day-head">
        <div>
          <p>{entry.date}</p>
          <h3>
            <a href={`/briefs/${entry.date}/`}>{title}</a>
          </h3>
        </div>
        <span>{entry.tools.length}</span>
      </div>
      <div className="history-tools">
        {entry.tools.slice(0, 6).map((tool) => (
          <a href={`/tools/${archiveSlug(tool.name)}/`} key={`${entry.date}-${tool.name}`}>
            <strong>{tool.name}</strong>
            <span>{language === "zh" ? tool.categoryZh ?? tool.category : tool.category}</span>
          </a>
        ))}
      </div>
    </article>
  );
}

export function App() {
  const [brief, setBrief] = useState<DailyBrief>(fallbackBrief);
  const [history, setHistory] = useState<RecommendationHistory | null>(null);
  const [loadedFromData, setLoadedFromData] = useState(false);
  const [language, setLanguage] = useState<Language>("zh");
  const copy = text[language];

  useEffect(() => {
    fetch(`/data/daily-brief.json?ts=${Date.now()}`)
      .then((response) => {
        if (!response.ok) throw new Error("daily brief not found");
        return response.json() as Promise<DailyBrief>;
      })
      .then((data) => {
        setBrief(data);
        setLoadedFromData(true);
      })
      .catch(() => setLoadedFromData(false));

    fetch(`/data/history.json?ts=${Date.now()}`)
      .then((response) => {
        if (!response.ok) throw new Error("history not found");
        return response.json() as Promise<RecommendationHistory>;
      })
      .then((data) => setHistory(data))
      .catch(() => setHistory(null));
  }, []);

  const topTool = brief.tools[0] ?? fallbackBrief.tools[0];
  const featuredTitle = language === "zh" ? brief.featured.titleZh ?? brief.featured.title : brief.featured.title;
  const featuredSummary =
    language === "zh" ? brief.featured.summaryZh ?? brief.featured.summary : brief.featured.summary;
  const featuredBody = language === "zh" ? brief.featured.bodyZh ?? brief.featured.body : brief.featured.body;
  const runLog = language === "zh" ? brief.runLogZh ?? brief.runLog : brief.runLog;
  const totalStars = useMemo(
    () => brief.tools.reduce((sum, item) => sum + item.githubStars, 0),
    [brief.tools]
  );

  return (
    <main lang={language === "zh" ? "zh-CN" : "en"}>
      <header className="site-header">
        <a className="brand" href="/">
          <span className="brand-mark">
            <Activity size={18} />
          </span>
          ToolPulse AI
        </a>
        <nav aria-label="Primary navigation">
          <a href="#today">{copy.nav[0]}</a>
          <a href="#radar">{copy.nav[1]}</a>
          <a href="#deep-dives">{copy.nav[2]}</a>
          <a href="#history">{copy.nav[3]}</a>
          <a href="#sources">{copy.nav[4]}</a>
        </nav>
        <div className="header-controls">
          <div className="language-switch" aria-label="Language switch">
            <button
              className={language === "zh" ? "active" : ""}
              type="button"
              onClick={() => setLanguage("zh")}
            >
              中文
            </button>
            <button
              className={language === "en" ? "active" : ""}
              type="button"
              onClick={() => setLanguage("en")}
            >
              EN
            </button>
          </div>
          <a className="header-action" href="#today">
            {copy.readBrief}
          </a>
        </div>
      </header>

      <section className="hero" id="today">
        <div className="hero-copy">
          <h1>{copy.heroTitle}</h1>
          <p>{copy.heroBody}</p>
          <div className="hero-actions">
            <a className="primary-action" href="#radar">
              {copy.openRadar} <Radar size={18} />
            </a>
            <a className="secondary-action" href={topTool.sourceUrl} target="_blank" rel="noreferrer">
              {copy.inspectSource} <GitBranch size={18} />
            </a>
          </div>
        </div>
        <div className="hero-metrics" aria-label="Daily automation status">
          <div>
            <strong>{brief.tools.length}</strong>
            <span>{copy.toolsAnalyzed}</span>
          </div>
          <div>
            <strong>{Math.round(totalStars / 1000)}k</strong>
            <span>{copy.starsScanned}</span>
          </div>
          <div>
            <strong>{formatDate(brief.generatedAt, language)}</strong>
            <span>{copy.lastRun}</span>
          </div>
        </div>
      </section>

      <section className="daily-grid" aria-label="Daily brief dashboard">
        <div className="panel ranked-panel">
          <div className="panel-heading">
            <div>
              <p>{copy.rankedTools}</p>
              <h2>{copy.signalOverVolume}</h2>
            </div>
            <Search size={20} />
          </div>
          <div className="tool-list">
            {brief.tools.slice(0, 5).map((tool) => (
              <ToolRow key={tool.name} tool={tool} language={language} />
            ))}
          </div>
        </div>

        <article className="panel feature-panel" id="deep-dives">
          <div className="feature-icon">
            <Sparkles size={22} />
          </div>
          <p className="section-label">{copy.featuredAnalysis}</p>
          <h2>{featuredTitle}</h2>
          <p className="feature-summary">{featuredSummary}</p>
          <p>{featuredBody}</p>
          <div className="feature-footer">
            <span className="verdict good">{copy.published}</span>
            <span>{loadedFromData ? copy.liveData : copy.seedBrief}</span>
          </div>
        </article>

        <aside className="panel operations-panel" id="sources">
          <div className="panel-heading">
            <div>
              <p>{copy.automation}</p>
              <h2>{copy.sourceHealth}</h2>
            </div>
            <Bot size={20} />
          </div>
          <div className="source-list">
            {brief.sourceHealth.map((source) => (
              <div className="source-item" key={source.name}>
                <div>
                  <strong>{source.name}</strong>
                  <span>{language === "zh" ? source.detailZh ?? source.detail : source.detail}</span>
                </div>
                <CheckCircle2 size={18} />
              </div>
            ))}
          </div>
          <div className="run-log">
            <div className="run-log-title">
              <Timer size={16} />
              {copy.runLog}
            </div>
            {runLog.map((item) => (
              <p key={item}>{item}</p>
            ))}
          </div>
        </aside>
      </section>

      <section className="radar-section" id="radar">
        <div className="section-heading">
          <div>
            <p>{copy.toolRadar}</p>
            <h2>{copy.evidence}</h2>
          </div>
          <div className="section-tools">
            <span>
              <Database size={15} />
              GitHub
            </span>
            <span>
              <RefreshCw size={15} />
              {copy.dailyJob}
            </span>
            <span>
              <ShieldCheck size={15} />
              {copy.linkChecks}
            </span>
          </div>
        </div>
        <div className="tool-card-grid">
          {brief.tools.map((tool) => (
            <ToolCard key={tool.name} tool={tool} language={language} />
          ))}
        </div>
      </section>

      <section className="history-section" id="history">
        <div className="section-heading">
          <div>
            <p>{copy.historyTitle}</p>
            <p className="history-description">{copy.historyBody}</p>
          </div>
          <div className="section-tools">
            <span>
              <History size={15} />
              {history?.entries.length ?? 0}
            </span>
            <span>{copy.archiveStatus}</span>
          </div>
        </div>
        {history?.entries.length ? (
          <div className="history-grid">
            {history.entries.slice(0, 12).map((entry) => (
              <HistoryDay entry={entry} language={language} key={entry.date} />
            ))}
          </div>
        ) : (
          <div className="history-empty">{copy.historyEmpty}</div>
        )}
      </section>
    </main>
  );
}
